import { SimpleTable } from '@core/components/Table';
import { TableActionButton, TableActionButtonDelete, TableActionButtonEdit } from '@core/components/TableAction';
import TableActionContainer from '@core/components/TableAction/TableActionContainer';
import { useApiUtils } from '@core/util/apiUtils';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { IconButton, Tooltip } from '@material-ui/core';
import EmailVisualizadoIcon from 'app/assets/icon/emailVisualizadoIcon';
import { fechamentoPeriodoApi } from 'app/services/api/fechamentoPeriodoApi';
import { vendaApi } from 'app/services/api/vendaApi';
import * as Actions from 'app/store/actions';
import 'jspdf-autotable';
import moment from 'moment';
import React, { useContext, useEffect } from 'react';
import { FaCheck, FaClipboardCheck, FaClock, FaEnvelope } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { VendaContext } from '../../VendaContext';
import VendaFab from '../VendaFab';
import { VendaModais } from '../modal/VendaModais';
import { vendasStyles } from './styles';

const getCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value).replace('R$', '');

const VendaTable = ({
  onSelection,
  fetchVendas,
  fetchVenda,
  onDelete,
  aprovarVenda,
  emitirPedidoVenda,
  handleOpenModalEnviarOrcamento,
  onTour,
}) => {
  const [vendas] = useContext(VendaContext).vendas;
  const [selecao] = useContext(VendaContext).selecao;
  const [loading, setLoading] = useContext(VendaContext).loading;
  const [, setModal] = useContext(VendaContext).modal;
  const [totalElements] = useContext(VendaContext).totalElements;
  const [search] = useContext(VendaContext).search;
  const [tipoSelecao, setTipoSelecao] = useContext(VendaContext).tipoSelecao;
  const [, setPage] = useContext(VendaContext).page;
  const [, setSize] = useContext(VendaContext).size;
  const [filtroAvancado] = useContext(VendaContext).filtroAvancado;

  const styles = vendasStyles();

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const location = useLocation();

  useEffect(() => {
    async function editarVendaFaturamento() {
      const { venda } = location;

      if (location.venda !== undefined) {
        await fetchVenda(venda.id);
        setModal(VendaModais.cadastrarVenda);
      }
    }

    editarVendaFaturamento();
    // Memoização intencional: as funções/props são recriadas a cada render e incluí-las
    // anularia o memo; o estado que elas leem já está nas dependências.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModalEditar = async (venda) => {
    setLoading(true);
    const response = await fechamentoPeriodoApi.buscarUltimoPeriodo('RECEBIMENTOS_VENDAS', venda.data);
    if (response.data && response.data.bloquearAnteriores) {
      setLoading(false);
      const vendaOp = venda.produtosServicos.filter((produtoServico) => produtoServico.produto);
      if (
        moment(venda.data).isSameOrBefore(response.data.periodo) &&
        response.data.funcionalidades.includes('RECEBIMENTOS_VENDAS')
      ) {
        if (vendaOp.length > 0) {
          throw apiUtils.handleApiError({
            message:
              'A venda de produto não pode ser editada porque a data refere-se a um período já fechado no sistema e não pode haver movimentações de estoque.',
          });
        } else {
          throw apiUtils.handleApiError({
            message:
              'Não é possível lançar, editar e excluir vendas com datas referentes a um período já fechado no sistema.',
          });
        }
      }
    }

    if (venda.situacao === 'VENDA_APROVADA') {
      if (venda?.possuiCobrancaBaixadaOuRemessaGerada) {
        setLoading(false);
        apiUtils.handleApiError({
          message:
            'A venda não pode ser editada porque já teve baixa ou alguma cobrança está em processo de geração de remessa.',
        });
      } else {
        await fetchVenda(venda.id);
        setModal(VendaModais.cadastrarVenda);
      }
    } else {
      await fetchVenda(venda.id);
      setModal(VendaModais.cadastrarVenda);
    }
  };

  const handleOpenModalVisualizar = async (venda) => {
    setLoading(true);
    const response = await vendaApi().getHistorico(venda.id);
    await fetchVenda(venda.id, response?.data);
    setModal(VendaModais.visualizarVenda);
  };

  const orcamentoAtrasado = (venda) => {
    let isOrcamentoAtrasado = false;
    if (venda?.dataVencimentoOrcamento) {
      const dataVencimento = moment(venda.dataVencimentoOrcamento);

      let dataAtual = new Date();
      dataAtual = moment(dataAtual.setHours(0, 0, 0, 0));

      isOrcamentoAtrasado = dataVencimento.isBefore(dataAtual);
    }
    return (
      isOrcamentoAtrasado &&
      venda?.situacao === 'EM_ORCAMENTO' && (
        <Tooltip title={`Orçamento vencido em ${moment(venda.dataVencimentoOrcamento).format('DD/MM/YYYY')}`}>
          <IconButton className={styles.buttonContainerAtrasado} onClick={() => {}}>
            <FaClock size={15} />
          </IconButton>
        </Tooltip>
      )
    );
  };

  const emailVisualizado = (venda) => {
    return (
      venda?.situacao === 'EM_ORCAMENTO' &&
      venda?.orcamentoVisualizado && (
        <Tooltip title="Orçamento visualizado">
          <IconButton className={styles.buttonContainerEmailVisualizado} onClick={() => {}}>
            <EmailVisualizadoIcon color="#3b948e" size={20} />
          </IconButton>
        </Tooltip>
      )
    );
  };

  const getSituacaoTag = (venda) => {
    const situacao = venda?.situacao;
    if (situacao === 'VENDA_APROVADA') {
      return (
        <>
          <div className={`${styles.situacaoContainer} ${styles.badge} ${styles.badgeVendaAprovada}`}>
            Venda aprovada
          </div>
        </>
      );
    }
    if (situacao === 'EM_ORCAMENTO') {
      return (
        <>
          <div className={`${styles.situacaoContainer} ${styles.badge} ${styles.badgeEmOrcamento}`}>Em orçamento</div>
        </>
      );
    }
    if (situacao === 'ORCAMENTO_RECUSADO') {
      return (
        <>
          <div className={`${styles.situacaoContainer} ${styles.badge} ${styles.badgeOrcamentoRecusado}`}>
            Orçamento recusado
          </div>
        </>
      );
    }
    return situacao;
  };

  const vendaColumns = [
    {
      title: 'Data da venda',
      name: 'data',
      getCellValue: (venda) => {
        return venda?.data ? moment(venda.data).format('DD/MM/YYYY') : '';
      },
      hideTooltip: true,
      sortingEnabled: true,
      align: 'center',
    },
    {
      title: 'Identificador',
      name: 'identificador',
      getCellValue: (venda) => {
        return venda?.identificador?.toString().padStart(3, '0');
      },
      sortingEnabled: true,
      align: 'center',
    },
    {
      title: 'Cliente',
      name: 'cliente.pessoa.nome',
      getCellValue: (venda) => {
        return venda?.nomeCliente;
      },
      sortingEnabled: true,
    },
    {
      title: 'Valor (R$)',
      name: 'valorTotal',
      hideTooltip: true,
      getCellValue: (venda) => {
        return getCurrency(venda?.valor);
      },
      sortingEnabled: true,
      align: 'right',
      width: 120,
    },
    {
      title: 'Situação',
      name: 'situacao',
      align: 'center',
      getCellValue: (venda) => {
        return getSituacaoTag(venda);
      },
      hideTooltip: true,
      sortingEnabled: true,
    },
    {
      title: '',
      name: ' ',
      sortingEnabled: false,
      align: 'left',
      hideTooltip: true,
      getCellValue: (venda) => {
        return (
          <>
            {orcamentoAtrasado(venda)}
            {emailVisualizado(venda)}
          </>
        );
      },
    },
    {
      name: '',
      title: '',
      sortingEnabled: false,
      align: 'left',
      width: 130,
      getCellValue: (venda) => (
        <TableActionContainer onTour={onTour}>
          <>
            {(permissaoUtils.venda().editar || onTour) && (
              <TableActionButtonEdit onClick={() => handleOpenModalEditar(venda)} className={'stepIconeEditar'} />
            )}
            {(permissaoUtils.venda().excluir || onTour) && (
              <TableActionButtonDelete
                onClick={() => onDelete({ ids: [venda], q: search, tipoSelecao: 'NENHUM', tipoFiltro: 'SIMPLES' })}
                className={'stepIconeExcluir'}
              />
            )}
            {(venda?.situacao !== 'VENDA_APROVADA' || onTour) && (
              <TableActionButton
                title="Aprovar venda"
                icon={<FaCheck size={10} className={'stepIconeAprovar'} />}
                onClick={() => aprovarVenda(venda)}
              />
            )}
            {(venda?.situacao === 'EM_ORCAMENTO' || onTour) && (
              <TableActionButton
                title="Enviar orçamento"
                icon={<FaEnvelope size={10} className={'stepIconeEnviar'} />}
                onClick={() => {
                  handleOpenModalEnviarOrcamento(venda.id);
                }}
              />
            )}
            {(venda?.situacao === 'VENDA_APROVADA' || onTour) && (
              <TableActionButton
                title="Emitir pedido de venda"
                icon={<FaClipboardCheck size={10} className={'stepIconeEmitir'} />}
                onClick={() => {
                  emitirPedidoVenda(venda);
                }}
              />
            )}
          </>
        </TableActionContainer>
      ),
    },
  ];

  return (
    <>
      <SimpleTable
        getRowId={(row) => row}
        columns={vendaColumns}
        rows={vendas}
        fetchData={fetchVendas}
        loading={loading}
        onRowClick={handleOpenModalVisualizar}
        defaultSorting={[{ columnName: 'data', direction: 'desc' }]}
        totalCount={totalElements}
        onCurrentPageChange={(params) => {
          setPage(params);
        }}
        onPageSizeChange={(params) => {
          setSize(params);
        }}
        selectionType={tipoSelecao}
        onSelectionTypeChange={(params) => {
          setTipoSelecao(params);
        }}
        selection={selecao}
        onSelectionChange={onSelection}
        onSortingChange={(params) => console.log(params)}
        onLoadingChange={(params) => console.log('loading change', params)}
        searchBy={search}
        params={filtroAvancado}
        contemFiltroAvancado
      />

      <VendaFab onDelete={onDelete} />
    </>
  );
};

export default VendaTable;
